import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { 
  BarChart3, TrendingUp, TrendingDown, ChevronDown, ChevronUp, 
  Loader2, CheckCircle, XCircle, RefreshCw 
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StatsSection({ embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [basicStats, setBasicStats] = useState(null);
  const [bankrollEvolution, setBankrollEvolution] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [basic, evolution, monthly] = await Promise.all([
        fetch(`${API_URL}/api/stats`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/stats/bankroll-evolution?days=90`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/stats/monthly-summary`, { headers }).then(r => r.json())
      ]);

      setBasicStats(basic);
      setBankrollEvolution(evolution);
      setMonthlyStats(monthly);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const stats = basicStats?.simple || {};
  const summary = bankrollEvolution?.summary || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-[#00E599]" />
      </div>
    );
  }

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded} className="w-full" data-testid="stats-section">
      <div className="bg-gradient-to-r from-[#6366F1]/10 to-[#00E599]/10 border border-[#6366F1]/30 rounded-xl overflow-hidden">
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors" data-testid="stats-toggle">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#6366F1]/20 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-[#6366F1]" />
              </div>
              <div className="text-left">
                <h3 className="font-heading text-sm font-bold text-white">ESTADÍSTICAS</h3>
                <p className="text-[10px] text-zinc-400">
                  Track record verificable
                  {stats.stats_baseline_since && (
                    <span
                      className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40"
                      data-testid="stats-baseline-badge"
                      title={`Desde ${stats.stats_baseline_since}. Histórico anterior archivado en /admin/markets-insights.`}
                    >
                      desde {stats.stats_baseline_since}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Quick Stats Preview */}
              <div className="hidden sm:flex items-center gap-2">
                <Badge className="bg-[#00E599]/20 text-[#00E599] border-0 text-[10px]">
                  {stats.win_rate || 0}% WR
                </Badge>
                <Badge className={`border-0 text-[10px] ${(stats.roi || 0) >= 0 ? 'bg-[#6366F1]/20 text-[#6366F1]' : 'bg-[#F43F5E]/20 text-[#F43F5E]'}`}>
                  {stats.roi >= 0 ? '+' : ''}{stats.roi || 0}% ROI
                </Badge>
                <Badge className={`border-0 text-[10px] ${(stats.yield || 0) >= 0 ? 'bg-[#F43F5E]/20 text-[#F43F5E]' : 'bg-zinc-700 text-zinc-300'}`}>
                  {(stats.yield || 0) >= 0 ? '+' : ''}{stats.yield || 0}% Yield
                </Badge>
                <Badge className={`border-0 text-[10px] ${(stats.profit_units || 0) >= 0 ? 'bg-[#00E599]/20 text-[#00E599]' : 'bg-[#F43F5E]/20 text-[#F43F5E]'}`}>
                  {stats.profit_units >= 0 ? '+' : ''}{stats.profit_units || 0}u
                </Badge>
              </div>
              {expanded ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-zinc-800 p-4 space-y-4">
            {/* Main Stats Cards - Now 5 columns with Yield */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <Card className="bg-gradient-to-br from-[#00E599]/20 to-zinc-900 border-[#00E599]/30">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-heading font-bold text-[#00E599]">{stats.win_rate || 0}%</p>
                  <p className="text-[10px] text-zinc-400 uppercase">Win Rate</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-[#6366F1]/20 to-zinc-900 border-[#6366F1]/30">
                <CardContent className="p-3 text-center">
                  <p className={`text-2xl font-heading font-bold ${stats.roi >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                    {stats.roi >= 0 ? '+' : ''}{stats.roi || 0}%
                  </p>
                  <p className="text-[10px] text-zinc-400 uppercase">ROI</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-[#F43F5E]/20 to-zinc-900 border-[#F43F5E]/30">
                <CardContent className="p-3 text-center">
                  <p className={`text-2xl font-heading font-bold ${(stats.yield || 0) >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                    {(stats.yield || 0) >= 0 ? '+' : ''}{stats.yield || 0}%
                  </p>
                  <p className="text-[10px] text-zinc-400 uppercase">Yield</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-[#EAB308]/20 to-zinc-900 border-[#EAB308]/30">
                <CardContent className="p-3 text-center">
                  <p className={`text-2xl font-heading font-bold ${stats.profit_units >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                    {stats.profit_units >= 0 ? '+' : ''}{stats.profit_units || 0}u
                  </p>
                  <p className="text-[10px] text-zinc-400 uppercase">Profit</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-heading font-bold text-white">{(stats.wins || 0) + (stats.losses || 0)}</p>
                  <p className="text-[10px] text-zinc-400 uppercase">Verificados</p>
                </CardContent>
              </Card>
            </div>

            {/* Record Display */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-3">
                <div className="flex items-center justify-center gap-6">
                  <div className="text-center">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle className="w-4 h-4 text-[#00E599]" />
                      <span className="text-xl font-heading font-bold text-[#00E599]">{stats.wins || 0}</span>
                    </div>
                    <p className="text-[10px] text-zinc-500">Aciertos</p>
                  </div>
                  <div className="text-2xl text-zinc-600">-</div>
                  <div className="text-center">
                    <div className="flex items-center gap-1.5">
                      <XCircle className="w-4 h-4 text-[#F43F5E]" />
                      <span className="text-xl font-heading font-bold text-[#F43F5E]">{stats.losses || 0}</span>
                    </div>
                    <p className="text-[10px] text-zinc-500">Fallos</p>
                  </div>
                </div>
                
                {/* Streaks */}
                {summary.best_winning_streak > 0 && (
                  <div className="flex justify-center gap-2 mt-3 flex-wrap">
                    <Badge className="bg-[#00E599]/20 text-[#00E599] border-0 text-[10px]">
                      Mejor: {summary.best_winning_streak}W
                    </Badge>
                    <Badge className="bg-[#F43F5E]/20 text-[#F43F5E] border-0 text-[10px]">
                      Peor: {summary.worst_losing_streak}L
                    </Badge>
                    <Badge className={`border-0 text-[10px] ${summary.current_streak > 0 ? 'bg-[#00E599]/20 text-[#00E599]' : 'bg-[#F43F5E]/20 text-[#F43F5E]'}`}>
                      Actual: {summary.current_streak > 0 ? `${summary.current_streak}W` : `${Math.abs(summary.current_streak || 0)}L`}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monthly Performance */}
            {monthlyStats?.monthly?.length > 0 && (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="pb-2 pt-3 px-3">
                  <CardTitle className="text-xs text-white flex items-center gap-2">
                    <TrendingUp className="w-3 h-3 text-[#00E599]" />
                    Rendimiento Mensual
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
                    {monthlyStats.monthly.slice(0, 5).map((month) => (
                      <div key={month.month} className="flex items-center justify-between text-[10px] bg-zinc-950 rounded px-2 py-1.5">
                        <span className="text-zinc-400 w-16">{month.month}</span>
                        <span className="text-white">{month.wins}W-{month.losses}L</span>
                        <span className={`font-bold ${month.win_rate >= 55 ? 'text-[#00E599]' : month.win_rate >= 50 ? 'text-[#EAB308]' : 'text-[#F43F5E]'}`}>
                          {month.win_rate}%
                        </span>
                        <span className={`font-bold ${month.profit >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                          {month.profit >= 0 ? '+' : ''}{month.profit}u
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Channels */}
            <div className="grid grid-cols-2 gap-2">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-3 text-center">
                  <p className="text-[10px] text-[#00E599] mb-1">Canal FREE</p>
                  <p className="text-lg font-bold text-white">
                    {basicStats?.by_channel?.free?.wins || 0}W - {basicStats?.by_channel?.free?.losses || 0}L
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-3 text-center">
                  <p className="text-[10px] text-[#EAB308] mb-1">Canal VIP</p>
                  <p className="text-lg font-bold text-white">
                    {basicStats?.by_channel?.vip?.wins || 0}W - {basicStats?.by_channel?.vip?.losses || 0}L
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Refresh Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={fetchStats}
              className="w-full h-8 text-xs border-zinc-700"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Actualizar Stats
            </Button>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
